// src/features/kommunikation/services/VersandnachweisService.ts
// Erzeugt einen Versand- und Abrufnachweis für eine versendete Lohnabrechnung
// als PDF, vollständig im Browser (pdf-lib). Ersteller ist die XTAX GmbH & Co. KG.
//
// Der Nachweis ist für den Arbeitnehmer bestimmt: Er belegt den Versand an die
// genannte Adresse, einen etwaigen Abruf über den geschützten Downloadbereich
// und enthält den Download-Link, damit der Arbeitnehmer seine Unterlagen selbst
// herunterladen kann. Interne Kennungen (Berater-/Mandantennummer, DATEV-
// Dokumententyp, interner Dateiname) werden bewusst nicht ausgewiesen.
//
// Datengrundlage:
//   - communication_documents  (Zugriffs-, Download- und Abrufdaten)
//   - communication_send_log   (tatsächlicher Empfänger, Betreff, Zeitpunkt,
//                                sowie die Gmail-Message-ID ab deren Erfassung)
//   - communication_credentials (Anrede/Nachname als Namensquelle)
//   - versand_zustellfehler    (Datenbankfunktion: endgültige Unzustellbarkeits-
//                                meldungen des Empfängerservers je Versand)
//
// Zustellfehler (Entscheidung 05.11.2026): Hat der Empfängerserver zu einem
// Versand eine endgültige Unzustellbarkeitsmeldung geschickt (z. B. Adresse
// existiert nicht, Postfach voll), weist der Nachweis das beim jeweiligen
// Versandvorgang mit Zeitpunkt, Grund und Code aus. Versände ohne solche
// Meldung erhalten den Vermerk, dass keine Anhaltspunkte für Zustellprobleme
// vorliegen. Lässt sich der
// Zustellstatus nicht prüfen, wird kein Nachweis erzeugt, damit er nie
// unvollständig ist.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { supabase } from '../../../shared/services/supabase';
import { CommunicationDocument, SendLog } from '../types';

// Send-Log-Zeile inkl. der additiv ergänzten Gmail-Kennung.
type SendLogRow = SendLog & { id: string; gmail_message_id: string | null };

// Ergebniszeile der Datenbankfunktion versand_zustellfehler.
interface ZustellfehlerRow {
  send_log_id: string;
  empfaenger: string | null;
  gemeldet_am: string;
  status_code: string | null;
  diagnose: string | null;
}

// Minimale Font-Signatur, die wir für die Breitenmessung brauchen.
interface MeasurableFont {
  widthOfTextAtSize: (text: string, size: number) => number;
}

// CP1252/WinAnsi kann die deutschen Umlaute und die westeuropäischen Akzente
// direkt darstellen. Für häufige ost-/südeuropäische Buchstaben, die in
// Zeitarbeits-Belegschaften vorkommen, transliterieren wir auf das Grundzeichen,
// damit der Name lesbar bleibt und die PDF-Erzeugung nie abbricht.
const TRANSLIT: Record<string, string> = {
  'ł': 'l', 'Ł': 'L', 'đ': 'd', 'Đ': 'D',
  'ı': 'i', 'İ': 'I', 'ş': 's', 'Ş': 'S', 'ğ': 'g', 'Ğ': 'G',
  'ą': 'a', 'ę': 'e', 'ć': 'c', 'ń': 'n', 'ś': 's', 'ź': 'z', 'ż': 'z',
  'Ą': 'A', 'Ę': 'E', 'Ć': 'C', 'Ń': 'N', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z',
  'č': 'c', 'ř': 'r', 'š': 's', 'ž': 'z', 'ě': 'e', 'ů': 'u', 'ď': 'd', 'ť': 't', 'ň': 'n',
  'Č': 'C', 'Ř': 'R', 'Š': 'S', 'Ž': 'Z', 'Ě': 'E', 'Ů': 'U',
  'ő': 'o', 'ű': 'u', 'Ő': 'O', 'Ű': 'U',
  'ā': 'a', 'ē': 'e', 'ī': 'i', 'ō': 'o', 'ū': 'u'
};

// In CP1252 undefinierte Positionen, die pdf-lib nicht kodieren kann.
const UNDEFINED_CP1252 = new Set([129, 141, 143, 144, 157]);

function sanitize(input: string | null | undefined): string {
  if (input === null || input === undefined) return '';
  let out = '';
  for (const ch of String(input)) {
    if (TRANSLIT[ch] !== undefined) {
      out += TRANSLIT[ch];
      continue;
    }
    const code = ch.charCodeAt(0);
    if (code <= 255 && !UNDEFINED_CP1252.has(code)) {
      out += ch;
    } else {
      out += '?';
    }
  }
  return out;
}

function wrapText(text: string, maxWidth: number, font: MeasurableFont, size: number): string[] {
  const clean = sanitize(text);
  const words = clean.split(/\s+/).filter(w => w.length > 0);
  const lines: string[] = [];
  let current = '';

  const pushLongWord = (word: string) => {
    let chunk = '';
    for (const ch of word) {
      if (font.widthOfTextAtSize(chunk + ch, size) <= maxWidth) {
        chunk += ch;
      } else {
        if (chunk) lines.push(chunk);
        chunk = ch;
      }
    }
    current = chunk;
  };

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      current = candidate;
    } else {
      if (current) {
        lines.push(current);
        current = '';
      }
      if (font.widthOfTextAtSize(word, size) > maxWidth) {
        pushLongWord(word);
      } else {
        current = word;
      }
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '(nicht vorhanden)';
  const d = new Date(iso);
  const formatted = d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/Berlin'
  });
  return `${formatted} Uhr`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '(nicht vorhanden)';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin'
  });
}

/**
 * Klartext zum DSN-Statuscode einer endgültigen Unzustellbarkeitsmeldung.
 * Codes ohne eigene Zuordnung gelten als Ablehnung durch den Empfängerserver.
 */
function zustellfehlerGrund(code: string | null): string {
  const c = (code || '').trim();
  if (c === '5.1.0' || c === '5.1.1' || c === '5.1.3') {
    return 'Die E-Mail-Adresse existiert nicht oder ist ungültig';
  }
  if (c === '5.1.2' || c === '5.1.10') {
    return 'Die Domain der E-Mail-Adresse existiert nicht oder empfängt keine E-Mails';
  }
  if (c === '5.2.2') {
    return 'Das Postfach des Empfängers ist voll';
  }
  if (c === '5.2.1') {
    return 'Das Postfach des Empfängers ist gesperrt oder deaktiviert';
  }
  return 'Der Empfängerserver hat die E-Mail abgelehnt';
}

export class VersandnachweisService {

  /**
   * Erzeugt den Versand- und Abrufnachweis für ein Dokument und startet den
   * Download der PDF-Datei im Browser.
   */
  async generateVersandnachweis(documentId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Dokument laden
      const { data: docData, error: docError } = await supabase
        .from('communication_documents')
        .select('*')
        .eq('id', documentId)
        .single();

      if (docError || !docData) {
        return { success: false, error: `Dokument nicht gefunden: ${docError?.message || 'Unbekannter Fehler'}` };
      }
      const docRow = docData as CommunicationDocument;

      // 2. Erfolgreiche Versandvorgänge laden (chronologisch)
      const { data: logData, error: logError } = await supabase
        .from('communication_send_log')
        .select('*')
        .eq('document_id', documentId)
        .eq('versand_erfolg', true)
        .order('versendet_am', { ascending: true });

      if (logError) {
        return { success: false, error: `Versandprotokoll konnte nicht geladen werden: ${logError.message}` };
      }
      const sendLogs = (logData || []) as SendLogRow[];

      // 2b. Zustellfehler prüfen. Ohne diese Prüfung wird kein Nachweis
      //     erzeugt, damit er nie unvollständig ist.
      const { data: fehlerData, error: fehlerError } = await supabase
        .rpc('versand_zustellfehler', { p_document_id: documentId });

      if (fehlerError) {
        return { success: false, error: `Zustellstatus konnte nicht geprüft werden: ${fehlerError.message}` };
      }
      const zustellfehler = (fehlerData || []) as ZustellfehlerRow[];
      const fehlerProVersand = new Map<string, ZustellfehlerRow>();
      for (const f of zustellfehler) {
        fehlerProVersand.set(f.send_log_id, f);
      }

      // 3. Name aus Credentials nachschlagen (best effort)
      let anrede: string | null = null;
      let nachname: string | null = null;
      try {
        let credQuery = supabase
          .from('communication_credentials')
          .select('anrede, nachname')
          .eq('mandantennummer', docRow.mandantennummer)
          .eq('personalnummer', docRow.personalnummer);
        if (docRow.beraternummer) {
          credQuery = credQuery.eq('beraternummer', docRow.beraternummer);
        }
        const { data: credRows } = await credQuery.limit(1);
        if (credRows && credRows.length > 0) {
          anrede = credRows[0].anrede ?? null;
          nachname = credRows[0].nachname ?? null;
        }
      } catch (e) {
        console.warn('[Versandnachweis] Credential-Lookup fehlgeschlagen:', e);
      }

      // Abgeleitete Anzeigewerte
      const name =
        docRow.mitarbeiter_vollname
        || [docRow.mitarbeiter_vorname, docRow.mitarbeiter_nachname].filter(Boolean).join(' ')
        || [anrede, nachname].filter(Boolean).join(' ')
        || '(nicht hinterlegt)';

      const downloadLink = `https://secure.xtax.de/dokument/${docRow.zugriffs_token}`;
      const passwortschutzAktiv =
        (docRow as unknown as { passwortschutz_aktiv?: boolean }).passwortschutz_aktiv !== false;

      // 4. PDF aufbauen
      const pdf = await PDFDocument.create();
      pdf.setTitle('Versand- und Abrufnachweis');
      pdf.setAuthor('XTAX GmbH & Co. KG');
      pdf.setCreator('XTAX GmbH & Co. KG');
      pdf.setProducer('XTAX GmbH & Co. KG');
      pdf.setSubject('Nachweis über den Versand einer Lohnabrechnung');
      pdf.setCreationDate(new Date());
      pdf.setModificationDate(new Date());

      const font = await pdf.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);

      const W = 595.28;
      const H = 841.89;
      const MARGIN = 50;
      const contentWidth = W - 2 * MARGIN;

      const dark = rgb(0.07, 0.09, 0.15);
      const gray = rgb(0.42, 0.45, 0.5);
      const accent = rgb(0, 0.4, 0.8);
      const lineColor = rgb(0.9, 0.9, 0.92);

      let page = pdf.addPage([W, H]);
      let y = H - MARGIN;

      const ensure = (needed: number) => {
        if (y - needed < MARGIN) {
          page = pdf.addPage([W, H]);
          y = H - MARGIN;
        }
      };

      const writeText = (text: string, x: number, size: number, f: typeof font, color: typeof dark) => {
        page.drawText(sanitize(text), { x, y, size, font: f, color });
      };

      const paragraph = (text: string, size: number, f: typeof font, color: typeof dark, lineHeight: number) => {
        const lines = wrapText(text, contentWidth, f, size);
        for (const ln of lines) {
          ensure(lineHeight);
          page.drawText(ln, { x: MARGIN, y, size, font: f, color });
          y -= lineHeight;
        }
      };

      const sectionHeader = (title: string) => {
        ensure(34);
        y -= 8;
        writeText(title, MARGIN, 12, fontBold, accent);
        y -= 8;
        page.drawLine({ start: { x: MARGIN, y }, end: { x: W - MARGIN, y }, thickness: 0.7, color: lineColor });
        y -= 16;
      };

      const row = (label: string, value: string) => {
        ensure(26);
        writeText(label, MARGIN, 9, fontBold, gray);
        y -= 13;
        const lines = wrapText(value, contentWidth, font, 11);
        for (const ln of lines) {
          ensure(15);
          page.drawText(ln, { x: MARGIN, y, size: 11, font, color: dark });
          y -= 15;
        }
        y -= 6;
      };

      // Kopf
      writeText('Versand- und Abrufnachweis', MARGIN, 18, fontBold, dark);
      y -= 22;
      writeText('XTAX GmbH & Co. KG - Lohnbuchhaltung', MARGIN, 10, font, gray);
      y -= 16;
      page.drawLine({ start: { x: MARGIN, y }, end: { x: W - MARGIN, y }, thickness: 1, color: accent });
      y -= 20;

      paragraph(
        'Dieser Nachweis dokumentiert den elektronischen Versand Ihrer Lohnabrechnung über das XTAX-Kommunikationssystem sowie einen etwaigen Abruf über den geschützten Downloadbereich.',
        10, font, gray, 14
      );
      y -= 4;

      // Arbeitnehmer
      sectionHeader('Arbeitnehmer');
      row('Name', name);
      row('Personalnummer', docRow.personalnummer);

      // Versand
      sectionHeader('Versand');
      if (sendLogs.length === 0) {
        row('Status', 'Kein erfolgreicher Versand protokolliert.');
      } else {
        sendLogs.forEach((log, idx) => {
          if (sendLogs.length > 1) {
            ensure(20);
            writeText(`Versandvorgang ${idx + 1} von ${sendLogs.length}`, MARGIN, 10, fontBold, dark);
            y -= 16;
          }
          row('Empfänger (E-Mail)', log.email_empfaenger);
          row('Versandzeitpunkt', formatDateTime(log.versendet_am));
          row('Betreff', log.email_betreff);
          row('Gmail-Message-ID', log.gmail_message_id || 'nicht erfasst (Versand vor Einführung der Kennungserfassung)');
          const fehler = fehlerProVersand.get(log.id);
          row(
            'Zustellung',
            fehler
              ? `Nicht zugestellt. Der Empfängerserver hat die E-Mail am ${formatDateTime(fehler.gemeldet_am)} zurückgewiesen: ${zustellfehlerGrund(fehler.status_code)} (Code ${fehler.status_code || 'unbekannt'}).`
              : 'Keine Anhaltspunkte für Zustellprobleme. Der Empfängerserver hat keine Unzustellbarkeitsmeldung zurückgesandt.'
          );
        });
      }

      // Abruf
      sectionHeader('Abruf über den geschützten Downloadbereich');
      if (docRow.zugegriffen_am) {
        row('Letzter dokumentierter Abruf', formatDateTime(docRow.zugegriffen_am));
        row('Anzahl der Abrufe', String(docRow.zugriffs_count ?? 0));
      } else {
        row('Status', 'Kein Abruf über den geschützten Link protokolliert.');
      }

      // Download
      sectionHeader('Download der Unterlagen');
      row('Link', downloadLink);
      if (passwortschutzAktiv) {
        row('Passwort', 'Die letzten 4 Ziffern Ihrer IBAN (Gehaltskonto)');
      }
      row('Link gültig bis', formatDate(docRow.token_ablaufdatum));

      // Hinweis
      sectionHeader('Hinweis');
      paragraph(
        'Der Nachweis belegt den Versand an die genannte E-Mail-Adresse und, sofern ein Abruf protokolliert ist, den Zugriff auf das Dokument über den geschützten Downloadbereich. Er ist kein Nachweis darüber, dass die Nachricht in das Postfach des Empfängers zugestellt oder dort gelesen wurde.',
        10, font, gray, 14
      );
      y -= 6;

      // Erzeugungsvermerk
      ensure(30);
      page.drawLine({ start: { x: MARGIN, y }, end: { x: W - MARGIN, y }, thickness: 0.7, color: lineColor });
      y -= 16;
      paragraph(
        `Erzeugt am ${formatDateTime(new Date().toISOString())}. Erstellt durch die XTAX GmbH & Co. KG.`,
        9, font, gray, 13
      );

      // 5. Download auslösen
      const bytes = await pdf.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const zeitraumSlug = docRow.ist_sonderversand
        ? 'Sonderversand'
        : (docRow.monat && docRow.jahr ? `${docRow.jahr}-${String(docRow.monat).padStart(2, '0')}` : 'ohne-Zeitraum');
      const heute = new Date().toISOString().slice(0, 10);
      const filename = `Versandnachweis_${docRow.personalnummer}_${zeitraumSlug}_${heute}.pdf`;

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return { success: true };

    } catch (error) {
      console.error('[Versandnachweis] Fehler bei der Erzeugung:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler bei der Nachweis-Erzeugung'
      };
    }
  }
}

export const versandnachweisService = new VersandnachweisService();
